/**
 * Host-level Campaign Detail overlay manager.
 *
 * Bridges the gap between the embedded opportunities iframe and the host page:
 * when a card click would normally open the in-iframe modal (clipped to the
 * iframe rect), the iframe instead posts `LEGION_REQUEST_CAMPAIGN_DETAIL` and
 * this manager mounts a viewport-filling overlay on the host page that loads
 * `/widget/campaign-detail/<id>` directly. The overlay is dismissed on ESC,
 * backdrop click, or a `CAMPAIGN_DETAIL_CLOSE` message from the detail iframe.
 *
 * The manager is a singleton: only one detail overlay can be open at a time,
 * and multiple `<legion-opportunities>` hosts on the same page share it.
 */

export interface OverlayHost {
  /** The custom element / wrapper that mounted the opportunities iframe. */
  element: HTMLElement;
  /** Returns the opportunities iframe currently rendered by this host. */
  iframeAccessor: () => HTMLIFrameElement | null;
  /** Base URL of the Legion instance serving the widgets. */
  baseUrl: () => string;
  /** Resolves the current auth token (attribute or auth bridge). */
  tokenAccessor?: () => string | null;
  /** Returns the active theme (passed through to the detail iframe URL). */
  themeAccessor?: () => string;
}

interface ActiveOverlay {
  overlayEl: HTMLDivElement;
  iframeEl: HTMLIFrameElement;
  host: OverlayHost;
  baseUrl: string;
  prevOverflow: string;
  onKey: (e: KeyboardEvent) => void;
  id: string;
}

const REQUEST_TYPE = "LEGION_REQUEST_CAMPAIGN_DETAIL";
const ACCEPTED_TYPE = "LEGION_CAMPAIGN_DETAIL_ACCEPTED";

const hosts = new Set<OverlayHost>();
let listenerAttached = false;
let active: ActiveOverlay | null = null;

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function findHostBySource(
  source: MessageEventSource | null
): OverlayHost | null {
  if (!source) return null;
  for (const host of hosts) {
    const iframe = host.iframeAccessor();
    if (iframe && iframe.contentWindow === source) return host;
  }
  return null;
}

function open(host: OverlayHost, id: string): void {
  close();

  const baseUrl = host.baseUrl();
  const theme = host.themeAccessor?.() ?? "light";

  const overlayEl = document.createElement("div");
  overlayEl.setAttribute("data-legion-overlay", "campaign-detail");
  Object.assign(overlayEl.style, {
    position: "fixed",
    inset: "0",
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    zIndex: "2147483000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  } as CSSStyleDeclaration);

  const container = document.createElement("div");
  Object.assign(container.style, {
    position: "relative",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "90vh",
    background: "transparent",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
  } as CSSStyleDeclaration);

  const iframeEl = document.createElement("iframe");
  const url = new URL(
    `/widget/campaign-detail/${encodeURIComponent(id)}`,
    baseUrl
  );
  url.searchParams.set("theme", theme);
  iframeEl.src = url.toString();
  iframeEl.title = "Campaign Detail";
  iframeEl.frameBorder = "0";
  iframeEl.scrolling = "auto";
  Object.assign(iframeEl.style, {
    width: "100%",
    height: "90vh",
    border: "none",
    background: "transparent",
    display: "block",
  } as CSSStyleDeclaration);

  iframeEl.addEventListener("load", () => {
    const token = host.tokenAccessor?.() ?? null;
    if (!iframeEl.contentWindow) return;
    try {
      if (token) {
        iframeEl.contentWindow.postMessage(
          { type: "CONFIG", apiKey: token, theme },
          baseUrl
        );
        iframeEl.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token },
          baseUrl
        );
      }
    } catch {
      // ignore postMessage failures
    }
  });

  container.appendChild(iframeEl);
  overlayEl.appendChild(container);
  document.body.appendChild(overlayEl);

  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  overlayEl.addEventListener("click", (e) => {
    if (e.target === overlayEl) close();
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);

  active = {
    overlayEl,
    iframeEl,
    host,
    baseUrl,
    prevOverflow,
    onKey,
    id,
  };
}

export function close(): void {
  if (!active) return;
  document.removeEventListener("keydown", active.onKey);
  if (active.overlayEl.parentNode) {
    active.overlayEl.parentNode.removeChild(active.overlayEl);
  }
  document.body.style.overflow = active.prevOverflow;
  const closed = active;
  active = null;
  closed.host.element.dispatchEvent(
    new CustomEvent("campaign-detail-close", {
      detail: { id: closed.id },
      bubbles: true,
      composed: true,
    })
  );
}

function onMessage(event: MessageEvent): void {
  const data = event.data;
  if (!data || typeof data !== "object") return;

  // Detail-overlay request from an opportunities iframe.
  if (data.type === REQUEST_TYPE && typeof data.id === "string" && data.id) {
    const host = findHostBySource(event.source);
    if (!host) return;
    const expected = originOf(host.baseUrl());
    if (expected && event.origin !== expected) return;
    open(host, data.id);
    try {
      (event.source as Window).postMessage(
        { type: ACCEPTED_TYPE, id: data.id },
        event.origin
      );
    } catch {
      // ignore
    }
    return;
  }

  // Messages from the detail iframe living inside our overlay.
  if (active && event.source === active.iframeEl.contentWindow) {
    const expected = originOf(active.baseUrl);
    if (expected && event.origin !== expected) return;
    if (data.type === "CAMPAIGN_DETAIL_CLOSE") {
      close();
    } else if (data.type === "CAMPAIGN_DETAIL_CLAIMED") {
      active.host.element.dispatchEvent(
        new CustomEvent("campaign-detail-claim", {
          detail: { id: data.id ? String(data.id) : null },
          bubbles: true,
          composed: true,
        })
      );
    } else if (
      data.type === "CAMPAIGN_DETAIL_REQUEST_LOGIN" ||
      data.type === "REQUEST_LOGIN"
    ) {
      active.host.element.dispatchEvent(
        new CustomEvent("campaign-detail-requestlogin", {
          detail: { id: data.id ? String(data.id) : null },
          bubbles: true,
          composed: true,
        })
      );
    }
  }
}

export function register(host: OverlayHost): () => void {
  hosts.add(host);
  if (!listenerAttached) {
    window.addEventListener("message", onMessage);
    listenerAttached = true;
  }
  return () => {
    hosts.delete(host);
    if (active && active.host === host) close();
    if (hosts.size === 0 && listenerAttached) {
      window.removeEventListener("message", onMessage);
      listenerAttached = false;
    }
  };
}
